import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { CalendarDays, MapPin, Trash2, BellRing, Clock, ArrowRight, CalendarPlus, Download, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { EVENT_CATEGORIES } from '../lib/eventData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pad = (n) => String(n).padStart(2, '0');
// Treat stored datetimes as floating wall-clock (strip any tz) so the device
// calendar shows the exact chosen time regardless of viewer timezone.
const toFloat = (iso) => {
  const [datePart, timeRaw = ''] = String(iso).split('T');
  const timePart = timeRaw.replace(/(\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/, '');
  const [y, mo, d] = datePart.split('-');
  const [h = '00', mi = '00', s = '00'] = timePart.split(':');
  return `${y}${mo}${d}T${pad(h)}${pad(mi)}${pad(s)}`;
};
const addHoursFloat = (iso, hours) => {
  const [datePart, timeRaw = ''] = String(iso).split('T');
  const tp = timeRaw.replace(/(\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/, '');
  const [y, mo, d] = datePart.split('-').map(Number);
  const [h = 0, mi = 0] = tp.split(':').map(Number);
  const dt = new Date(y, mo - 1, d, h, mi);
  dt.setHours(dt.getHours() + hours);
  return `${dt.getFullYear()}${pad(dt.getMonth() + 1)}${pad(dt.getDate())}T${pad(dt.getHours())}${pad(dt.getMinutes())}00`;
};
const utcStamp = () => {
  const d = new Date();
  return `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`;
};
const locText = (entry) => [entry.location_name, entry.address].filter(Boolean).join(', ') || entry.city || '';
const rangeFloat = (entry) => `${toFloat(entry.event_date)}/${entry.end_date ? toFloat(entry.end_date) : addHoursFloat(entry.event_date, 2)}`;

const googleUrl = (entry) => {
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: entry.title || '',
    dates: rangeFloat(entry),
    details: entry.description || '',
    location: locText(entry),
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

const downloadIcs = (entry) => {
  const esc = (s) => (s || '').replace(/\\/g, '\\\\').replace(/,/g, '\\,').replace(/;/g, '\\;').replace(/\n/g, '\\n');
  const end = entry.end_date ? toFloat(entry.end_date) : addHoursFloat(entry.event_date, 2);
  const lines = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//KoLV//Kalendars//LV', 'CALSCALE:GREGORIAN', 'BEGIN:VEVENT',
    `UID:${entry.event_id}@kolv.lv`, `DTSTAMP:${utcStamp()}`,
    `DTSTART:${toFloat(entry.event_date)}`, `DTEND:${end}`,
    `SUMMARY:${esc(entry.title)}`,
    locText(entry) ? `LOCATION:${esc(locText(entry))}` : '',
    'END:VEVENT', 'END:VCALENDAR',
  ].filter(Boolean);
  const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(entry.title || 'notikums').replace(/[^\w\-]+/g, '_').slice(0, 40)}.ics`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
};

const REMINDER_LABELS = {
  60: { lv: '1h iepriekš', en: '1h before', ru: 'за 1ч' },
  1440: { lv: '1 dienu iepriekš', en: '1 day before', ru: 'за 1 день' },
  10080: { lv: '1 nedēļu iepriekš', en: '1 week before', ru: 'за 1 неделю' },
};

function CalRow({ entry, onRemove, language }) {
  const tr = (lv, en, ru) => (language === 'lv' ? lv : language === 'ru' ? ru : en);
  const cat = EVENT_CATEGORIES[entry.category];
  const isBooking = entry.source === 'booking';
  const dateStr = entry.event_date
    ? new Date(entry.event_date).toLocaleDateString(language === 'lv' ? 'lv-LV' : language === 'ru' ? 'ru-RU' : 'en-US',
      { weekday: 'short', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })
    : tr('Datums nav norādīts', 'No date', 'Дата не указана');
  const rem = REMINDER_LABELS[entry.reminder_minutes];
  const linkTo = isBooking ? `/uznemums/${entry.business_user_id}` : `/events/${entry.event_id}`;
  return (
    <div className="bg-white border border-[#E5E4E0] rounded-xl p-4 flex items-center gap-4" data-testid={`calendar-entry-${entry.event_id}`}>
      <div className={`flex flex-col items-center justify-center rounded-lg w-14 h-14 shrink-0 ${isBooking ? 'bg-[#FFF6F0]' : 'bg-[#F3F2EE]'}`}>
        <CalendarDays className={`w-5 h-5 ${isBooking ? 'text-[#D97736]' : 'text-[#4A6B53]'}`} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={linkTo} className="font-outfit font-semibold text-[#121212] hover:text-[#D97736] truncate">{entry.title}</Link>
          {isBooking
            ? <Badge className="bg-[#FFF1E6] text-[#D97736] border-0 text-xs">{tr('Rezervācija', 'Reservation', 'Бронь')}</Badge>
            : cat && <Badge className="bg-[#F3F2EE] text-[#5C5C5C] border-0 text-xs">{cat[language] || cat.lv}</Badge>}
        </div>
        <div className="flex items-center gap-3 text-xs text-[#5C5C5C] mt-1 flex-wrap">
          <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{dateStr}</span>
          {entry.location_name && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{entry.location_name}</span>}
          {rem && <span className="flex items-center gap-1 text-[#D97736]"><BellRing className="w-3.5 h-3.5" />{rem[language] || rem.lv}</span>}
        </div>
      </div>
      {entry.event_date && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="shrink-0 border-[#E5E4E0] text-[#5C5C5C] hover:border-[#D97736] hover:text-[#D97736]" data-testid={`calendar-export-${entry.event_id}`}>
              <CalendarPlus className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">{tr('Pievienot', 'Add', 'Добавить')}</span><ChevronDown className="w-3.5 h-3.5 ml-1 opacity-80" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56" data-testid={`calendar-export-menu-${entry.event_id}`}>
            <DropdownMenuItem asChild data-testid={`export-google-${entry.event_id}`}>
              <a href={googleUrl(entry)} target="_blank" rel="noopener noreferrer">
                <CalendarPlus className="w-4 h-4 mr-2 text-[#4285F4]" /> Google Calendar
              </a>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => downloadIcs(entry)} data-testid={`export-ics-${entry.event_id}`}>
              <Download className="w-4 h-4 mr-2 text-[#5C5C5C]" /> {tr('.ics (Apple / Outlook)', '.ics (Apple / Outlook)', '.ics (Apple / Outlook)')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      <button onClick={() => onRemove(entry.event_id)} className="p-2 rounded-full hover:bg-red-50 text-red-500 shrink-0" data-testid={`calendar-remove-${entry.event_id}`} title={tr('Noņemt', 'Remove', 'Убрать')}>
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function CalendarPage() {
  const { language } = useLanguage();
  const tr = (lv, en, ru) => (language === 'lv' ? lv : language === 'ru' ? ru : en);
  const [tab, setTab] = useState('upcoming');
  const [data, setData] = useState({ upcoming: [], past: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/me/calendar`, { withCredentials: true });
      setData(data);
    } catch (e) { setData({ upcoming: [], past: [] }); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (eventId) => {
    try {
      await axios.delete(`${API}/events/${eventId}/attend`, { withCredentials: true });
      toast(tr('Noņemts no kalendāra', 'Removed from calendar', 'Удалено из календаря'));
      load();
    } catch (e) { /* ignore */ }
  };

  const list = tab === 'upcoming' ? data.upcoming : data.past;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="calendar-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="font-outfit text-3xl font-bold text-[#121212] flex items-center gap-2">
              <CalendarDays className="w-7 h-7 text-[#4A6B53]" /> {tr('Mans kalendārs', 'My calendar', 'Мой календарь')}
            </h1>
            <p className="text-[#5C5C5C] text-sm mt-1">{tr('Pasākumi un rezervācijas, kam esi pieteicies. Atgādināsim laikus.',
              'Events and reservations you signed up for. We will remind you in time.',
              'События и брони, на которые вы записались. Напомним вовремя.')}</p>
          </div>
          <Link to="/events"><Button variant="outline" className="border-[#E5E4E0]" data-testid="browse-events-btn">{tr('Pārlūkot pasākumus', 'Browse events', 'Смотреть события')} <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mb-6">
          <TabsList data-testid="calendar-tabs">
            <TabsTrigger value="upcoming" data-testid="calendar-tab-upcoming">{tr('Gaidāmie', 'Upcoming', 'Предстоящие')} ({data.upcoming.length})</TabsTrigger>
            <TabsTrigger value="past" data-testid="calendar-tab-past">{tr('Pagājušie', 'Past', 'Прошедшие')} ({data.past.length})</TabsTrigger>
          </TabsList>
        </Tabs>

        {loading ? (
          <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="bg-white rounded-xl h-20 animate-pulse" />)}</div>
        ) : list.length === 0 ? (
          <div className="bg-white border border-dashed border-[#E5E4E0] rounded-xl p-12 text-center text-[#5C5C5C]" data-testid="calendar-empty">
            {tab === 'upcoming'
              ? tr('Vēl nav gaidāmu pasākumu. Piesakies kādam un tas parādīsies šeit!', 'No upcoming events yet. Sign up for one and it will appear here!', 'Пока нет предстоящих событий.')
              : tr('Nav pagājušu pasākumu.', 'No past events.', 'Нет прошедших событий.')}
          </div>
        ) : (
          <div className="space-y-3" data-testid="calendar-list">
            {list.map((e) => <CalRow key={e.event_id} entry={e} onRemove={remove} language={language} />)}
          </div>
        )}
      </div>
    </div>
  );
}
